import React, { useState, useEffect, useRef, useMemo } from 'react';
import WalletManagement from '../components/layout/WalletManagement';
import WalletAnalytics from '../components/layout/WalletAnalytics';
import {
  useOrdConnect,
  OrdConnectProvider,
  Chain,
  Wallet,
} from '@ordzaar/ord-connect';
import { useConnect } from '../hooks/useConnect.ts';
import { useBitprint, BitprintProvider } from '../modules/bitprint.tsx';
import { fetchWalletOrdinals } from '../utils/autoTradingUtils';
import './Analytics.css';
import lineImage from '../assets/images/png/line.png';
import leather_icon from '../assets/images/svg/leather.svg';
import magic_icon from '../assets/images/svg/magic_icon.svg';
import okx_icon from '../assets/images/svg/okx.svg';
import unisat_icon from '../assets/images/svg/unisat.svg';
import xverse_icon from '../assets/images/svg/xverse-icon.svg';

// Wallet list configuration
const CONNECT_WALLET_LIST = [
  {
    wallet: Wallet.OKX,
    icon: okx_icon,
    order: 1,
    chains: [Chain.BITCOIN],
  },
  {
    wallet: Wallet.UNISAT,
    icon: unisat_icon,
    order: 2,
    chains: [Chain.BITCOIN, Chain.FRACTAL_BITCOIN],
  },
  {
    wallet: Wallet.XVERSE,
    icon: xverse_icon,
    order: 3,
    chains: [Chain.BITCOIN],
  },
  {
    wallet: Wallet.MAGICEDEN,
    icon: magic_icon,
    order: 4,
    chains: [Chain.BITCOIN],
  },
  {
    wallet: Wallet.LEATHER,
    icon: leather_icon,
    order: 5,
    chains: [Chain.BITCOIN],
  },
];

const ordinalPreviewUrl = (item) => {
  const raw = item._satflowRaw;
  const token = raw?.token || {};
  const ct = item.contentType || token.content_type;
  if (ct && String(ct).startsWith('image/') && item.contentURI) {
    return item.contentURI;
  }
  return (
    token.image_url || raw?.collection?.image_url || item.contentURI || null
  );
};

const ordinalContentIsHtml = (item) => {
  const raw = item._satflowRaw;
  const token = raw?.token || {};
  const ct = item.contentType || token.content_type;
  if (!ct) return false;
  return String(ct).toLowerCase().includes('html');
};

const ordinalContentIsText = (item) => {
  const raw = item._satflowRaw;
  const token = raw?.token || {};
  const ct = item.contentType || token.content_type;
  if (!ct) return false;
  const s = String(ct).toLowerCase();
  if (!s.startsWith('text/')) return false;
  return !s.includes('html');
};

const OrdinalTextPreview = ({ url }) => {
  const [text, setText] = useState('');
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setText('');
    fetch(url, { credentials: 'omit' })
      .then((res) => {
        if (!res.ok) throw new Error('fetch failed');
        return res.text();
      })
      .then((body) => {
        if (!cancelled) {
          setText(body);
          setStatus('ok');
        }
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (status === 'loading') {
    return (
      <div className="analytics-ordinals-card-text-loading">Loading text…</div>
    );
  }
  if (status === 'error') {
    return (
      <div className="analytics-ordinals-card-placeholder">
        Text could not be loaded (blocked or unavailable).
      </div>
    );
  }
  return (
    <textarea
      readOnly
      className="analytics-ordinals-card-text"
      value={text}
      aria-label="Inscription text content"
    />
  );
};

const ordinalDisplayName = (item) => {
  const raw = item._satflowRaw;
  const token = raw?.token || {};
  return (
    token.name ||
    (item.inscriptionNumber != null
      ? `Inscription #${item.inscriptionNumber}`
      : item.inscriptionId?.slice(0, 16) + '…') ||
    'Ordinal'
  );
};

/**
 * Fetches Satflow wallet-contents for the sidebar-selected proxy wallet only
 * (/api/satflow-wallet-contents via setupProxy).
 */
const ProxyWalletOrdinalsSection = ({ glEventHub }) => {
  const [proxyWallets, setProxyWallets] = useState([]);
  const [selectedProxyIndex, setSelectedProxyIndex] = useState(null);
  const [row, setRow] = useState(null);

  useEffect(() => {
    const onGenerated = (wallets) => {
      setProxyWallets(Array.isArray(wallets) ? wallets : []);
    };
    const onSelect = (wallet) => {
      if (wallet && typeof wallet.index === 'number') {
        setSelectedProxyIndex(wallet.index);
      } else {
        setSelectedProxyIndex(null);
      }
    };
    if (glEventHub) {
      glEventHub.on('wallets-generated', onGenerated);
      glEventHub.on('wallet-selected', onSelect);
    }
    return () => {
      if (glEventHub) {
        glEventHub.off('wallets-generated', onGenerated);
        glEventHub.off('wallet-selected', onSelect);
      }
    };
  }, [glEventHub]);

  useEffect(() => {
    let cancelled = false;

    const w = proxyWallets.find((pw) => pw.index === selectedProxyIndex);
    if (selectedProxyIndex == null || !w || proxyWallets.length === 0) {
      setRow(null);
      return;
    }

    const address = w.address || w.addresses?.p2tr;
    const label = `Proxy wallet #${w.index + 1}`;

    setRow({
      label,
      address,
      ordinals: [],
      loading: true,
      error: null,
    });

    (async () => {
      if (!address) {
        if (!cancelled) {
          setRow({
            label,
            address: null,
            ordinals: [],
            loading: false,
            error: 'No address',
          });
        }
        return;
      }
      try {
        const ordinals = await fetchWalletOrdinals(address, null, true);
        if (!cancelled) {
          setRow({
            label,
            address,
            ordinals,
            loading: false,
            error: null,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setRow({
            label,
            address,
            ordinals: [],
            loading: false,
            error: e?.message || 'Failed to load',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [proxyWallets, selectedProxyIndex]);

  const formatAddr = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 10)}…${addr.slice(-8)}`;
  };

  if (proxyWallets.length === 0) {
    return (
      <section className="analytics-ordinals-section">
        <h2 className="component-header">Wallet ordinals</h2>
        <div className="analytics-ordinals-empty">
          <p className="analytics-ordinals-empty-text">
            Generate proxy wallets in the sidebar, then select one to view its
            ordinals from Satflow.
          </p>
        </div>
      </section>
    );
  }

  if (selectedProxyIndex == null) {
    return (
      <section className="analytics-ordinals-section">
        <h2 className="component-header">Wallet ordinals</h2>
        <div className="analytics-ordinals-empty">
          <p className="analytics-ordinals-empty-text">
            Select a proxy wallet in the sidebar to load its ordinals.
          </p>
        </div>
      </section>
    );
  }

  const selectedWallet = proxyWallets.find(
    (pw) => pw.index === selectedProxyIndex
  );
  if (!selectedWallet) {
    return (
      <section className="analytics-ordinals-section">
        <h2 className="component-header">Wallet ordinals</h2>
        <div className="analytics-ordinals-empty">
          <p className="analytics-ordinals-empty-text">
            That proxy wallet is not in the current list. Select a wallet from
            the sidebar.
          </p>
        </div>
      </section>
    );
  }

  if (!row) {
    return (
      <section className="analytics-ordinals-section">
        <h2 className="component-header">Wallet ordinals</h2>
        <div className="analytics-ordinals-loading">
          Loading wallet ordinals…
        </div>
      </section>
    );
  }

  return (
    <section className="analytics-ordinals-section">
      <h2 className="component-header">Wallet ordinals</h2>

      <div className="analytics-ordinals-wallet-block analytics-ordinals-wallet-block--selected">
        <div className="analytics-ordinals-wallet-header">
          <span className="analytics-ordinals-wallet-label">{row.label}</span>
          <span className="analytics-ordinals-wallet-address">
            {formatAddr(row.address)}
          </span>
        </div>

        {row.loading ? (
          <div className="analytics-ordinals-loading">Loading ordinals…</div>
        ) : row.error ? (
          <div className="analytics-ordinals-error">{row.error}</div>
        ) : row.ordinals.length === 0 ? (
          <div className="analytics-ordinals-none">
            No ordinals in this wallet.
          </div>
        ) : (
          <div className="analytics-ordinals-grid">
            {row.ordinals.map((item, idx) => {
              const preview = ordinalPreviewUrl(item);
              const name = ordinalDisplayName(item);
              const coll =
                item._satflowRaw?.collection?.name ||
                item.collectionSymbol ||
                '';
              const inscriptionId =
                item.inscriptionId || item._satflowRaw?.token?.inscription_id;
              return (
                <article
                  key={inscriptionId || idx}
                  className="analytics-ordinals-card"
                >
                  <div className="analytics-ordinals-card-media">
                    {preview ? (
                      ordinalContentIsHtml(item) ? (
                        <iframe
                          title={name}
                          src={preview}
                          className="analytics-ordinals-card-iframe"
                          sandbox="allow-scripts allow-same-origin"
                          loading="lazy"
                        />
                      ) : ordinalContentIsText(item) ? (
                        <OrdinalTextPreview url={preview} />
                      ) : (
                        <img
                          src={preview}
                          alt=""
                          loading="lazy"
                          className="analytics-ordinals-card-img"
                        />
                      )
                    ) : (
                      <div className="analytics-ordinals-card-placeholder">
                        No preview
                      </div>
                    )}
                  </div>
                  <div className="analytics-ordinals-card-body">
                    <div className="analytics-ordinals-card-title" title={name}>
                      {name}
                    </div>
                    {coll && (
                      <div className="analytics-ordinals-card-collection">
                        {coll}
                      </div>
                    )}
                    {item.inscriptionNumber != null && (
                      <div className="analytics-ordinals-card-meta">
                        #{item.inscriptionNumber}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

// Inner component that uses the hooks
const AnalyticsInner = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const dropdownRef = useRef(null);

  // Create a shared event hub for WalletManagement and WalletAnalytics
  const glEventHub = useMemo(() => {
    const events = {};
    return {
      on: (event, handler) => {
        if (!events[event]) events[event] = [];
        events[event].push(handler);
      },
      off: (event, handler) => {
        if (events[event]) {
          events[event] = events[event].filter((h) => h !== handler);
        }
      },
      emit: (event, data) => {
        if (events[event]) {
          events[event].forEach((handler) => handler(data));
        }
      },
    };
  }, []);

  const {
    network,
    disconnectWallet,
    address: connectedAddress,
    publicKey: connectedPublicKey,
    format: connectedFormat,
    wallet: connectedWallet,
    chain,
    updateNetwork,
  } = useOrdConnect();

  const { globalState: bitprint } = useBitprint();

  const { connectWallet } = useConnect({
    onClose: () => {},
    onError: (err) => {
      setErrorMessage(err);
    },
  });

  // Check if wallet is connected
  const isWalletConnected =
    connectedAddress &&
    connectedAddress.ordinals &&
    !(bitprint && bitprint.isDisconnected);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle wallet connection
  const handleConnect = async (wallet) => {
    console.log('Attempting to connect wallet:', wallet);
    setErrorMessage('');

    if (bitprint) {
      bitprint.isDisconnected = false;
    }

    localStorage.removeItem('wallet-disconnected');

    try {
      const walletConnectPromise = connectWallet(wallet);
      const result = await Promise.race([
        walletConnectPromise,
        new Promise((resolve) => setTimeout(() => resolve('timeout'), 5000)),
      ]);

      if (typeof result === 'string') {
        setErrorMessage(
          'No wallet pop-up? The extension is not responding. Try reloading your browser.'
        );
      } else {
        console.log('Wallet connected successfully:', result);
        setIsDropdownOpen(false);

        // Always refresh the page after a successful wallet connection
        // This ensures all components see the new connection state
        window.location.reload();
      }
    } catch (error) {
      console.error('Wallet connection error:', error);
      setErrorMessage('Failed to connect wallet. Please try again.');
    }
  };

  // Handle disconnect
  const handleDisconnect = async () => {
    try {
      console.log('Attempting to disconnect wallet');

      if (bitprint && bitprint.disconnect) {
        bitprint.disconnect();
      }

      localStorage.setItem('wallet-disconnected', 'true');
      disconnectWallet();
      setIsDropdownOpen(false);

      // Always refresh the page after disconnecting the wallet
      // This ensures all components reset to the disconnected state
      window.location.reload();
    } catch (error) {
      console.error('Error disconnecting wallet:', error);
      setErrorMessage('Failed to disconnect wallet. Please try again.');
    }
  };

  // Format address
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 8)}...${address.slice(-8)}`;
  };

  // Format string for display
  const formatString = (str) => {
    if (str && str.length > 14) {
      return `${str.slice(0, 7)}...${str.slice(-7)}`;
    }
    return str || '';
  };

  // Handle network change
  const handleNetworkChange = async (event) => {
    const selectedNetwork = event.target.value;
    updateNetwork(selectedNetwork);

    try {
      await connectWallet(connectedWallet);
    } catch {
      // Error reconnecting wallet
    }
  };

  return (
    <div className="analytics-container">
      <div className="analytics-container-content">
        {/* Header */}
        <header className="analytics-header">
          <div className="analytics-header-content">
            <h1 className="analytics-title">WALLET ANALYTICS</h1>

            <div className="analytics-wallet-section" ref={dropdownRef}>
              <button
                className="analytics-connect-button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                {isWalletConnected
                  ? formatAddress(connectedAddress.ordinals)
                  : 'Connect'}
              </button>

              {isDropdownOpen && (
                <div className="analytics-dropdown">
                  {!isWalletConnected ? (
                    <>
                      <div className="analytics-dropdown-header">
                        Connect Wallet
                      </div>
                      <div className="analytics-wallet-list">
                        {CONNECT_WALLET_LIST.map((walletItem, i) => (
                          <button
                            key={i}
                            className="analytics-wallet-item"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleConnect(walletItem.wallet);
                            }}
                          >
                            <img
                              src={walletItem.icon}
                              alt={`${walletItem.wallet} icon`}
                              className="analytics-wallet-icon"
                            />
                            <span className="analytics-wallet-name">
                              {walletItem.wallet}
                            </span>
                          </button>
                        ))}
                      </div>
                      {errorMessage && (
                        <div className="analytics-error-message">
                          {errorMessage}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="analytics-dropdown-header">
                        Wallet Info
                      </div>
                      <div className="analytics-wallet-info">
                        <div className="analytics-info-row">
                          <span className="analytics-info-label">Address:</span>
                          <span className="analytics-info-value">
                            {formatString(connectedAddress.ordinals)}
                          </span>
                        </div>
                        <div className="analytics-info-row">
                          <span className="analytics-info-label">
                            Public Key:
                          </span>
                          <span className="analytics-info-value">
                            {formatString(connectedPublicKey.ordinals)}
                          </span>
                        </div>
                        <div className="analytics-info-row">
                          <span className="analytics-info-label">Format:</span>
                          <span className="analytics-info-value">
                            {connectedFormat.ordinals}
                          </span>
                        </div>
                        <div className="analytics-info-row">
                          <span className="analytics-info-label">Chain:</span>
                          <span className="analytics-info-value">{chain}</span>
                        </div>
                        <div className="analytics-info-row">
                          <span className="analytics-info-label">Network:</span>
                          <select
                            onChange={handleNetworkChange}
                            className="analytics-network-select"
                            value={network}
                          >
                            <option value="mainnet">mainnet</option>
                            <option value="testnet">testnet</option>
                            <option value="signet">signet</option>
                          </select>
                        </div>
                        <button
                          onClick={handleDisconnect}
                          className="analytics-disconnect-button"
                        >
                          Disconnect
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <img
            src={lineImage}
            alt="separator"
            className="analytics-header-separator"
          />
        </header>

        {/* Main Content */}
        <div className="analytics-main">
          {/* Sidebar */}
          <aside className="analytics-sidebar">
            <WalletManagement glEventHub={glEventHub} />
          </aside>
          <div className="analytics-sidebar-separator-container">
            <img
              src={lineImage}
              alt="separator"
              className="analytics-sidebar-separator"
            />
          </div>

          {/* Content Area */}
          <main className="analytics-content">
            <WalletAnalytics glEventHub={glEventHub} network={network} />
            <ProxyWalletOrdinalsSection glEventHub={glEventHub} />
          </main>
        </div>
      </div>
    </div>
  );
};

// Main component with providers
const Analytics = () => {
  return (
    <BitprintProvider>
      <OrdConnectProvider network="mainnet" chain="bitcoin" ssr={true}>
        <AnalyticsInner />
      </OrdConnectProvider>
    </BitprintProvider>
  );
};

export default Analytics;
